import React from 'react';
import { useTranslation } from 'react-i18next';
import { FarmsOverviewService } from './FarmsOverviewService';
import { FarmsOverview, FarmsOverviewQuery } from '../models/farms-overview';
import { extractApiErrorMessage, notifications } from '../utils';

interface UseFarmsOverviewResult {
  overview: FarmsOverview | null;
  isBusy: boolean;
  /** True once a fetch has settled, successfully or not - lets callers tell "empty" from "not asked yet". */
  loaded: boolean;
  reload: () => void;
}

/**
 * Loads the admin farms overview for the current query.
 *
 * Search, rule, severity, status, order and paging are all server-side, so every change to them refetches - which is
 * why the caller debounces the search box rather than the hook debouncing itself: only the caller knows
 * which part of the query a keystroke touched.
 */
export const useFarmsOverview = (query: FarmsOverviewQuery): UseFarmsOverviewResult => {
  const { t } = useTranslation();
  const [overview, setOverview] = React.useState<FarmsOverview | null>(null);
  const [isBusy, setIsBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [reloadToken, setReloadToken] = React.useState(0);

  const { searchText, rule, severity, status, weighing, pendingOnly, noAntibioticsOnly, sort, page, size } = query;

  React.useEffect(() => {
    const service = new FarmsOverviewService();
    // The query can change while a request is in flight; ignore anything but the newest one.
    let cancelled = false;

    setIsBusy(true);

    service
      .getOverview({ searchText, rule, severity, status, weighing, pendingOnly, noAntibioticsOnly, sort, page, size })
      .then((result) => {
        if (cancelled) return;
        setOverview(result);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setOverview(null);
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        if (cancelled) return;
        setIsBusy(false);
        setLoaded(true);
      });

    return () => {
      cancelled = true;
    };
    // Destructured rather than depending on `query`, which is a fresh object on every render.
  }, [searchText, rule, severity, status, weighing, pendingOnly, noAntibioticsOnly, sort, page, size, reloadToken]);

  const reload = React.useCallback(() => setReloadToken((token) => token + 1), []);

  return { overview, isBusy, loaded, reload };
};

export default useFarmsOverview;
